import type { CharacterGridSpec } from "@/character/grid";
import { SKILL_DEFAULT_BUFF_DURATION_MS, SKILL_SLOT_MAX } from "@/character/skills/constants";
import type { SkillFrameLayout } from "@/character/skills/skill-frame-layout";
import type { StatId } from "@/game/model/character-stats";

export type SkillKind = "attack" | "buff";

/** Attack skills: single target vs 3×3 grid (1 cell) around the player. */
export type SkillAttackPattern = "single" | "aoe";

export type SkillDefinition = {
  id: string;
  name: string;
  kind: SkillKind;
  /** Buff only — how long the VFX stays on the player. */
  durationMs: number;
  spritesheetDataUrl: string;
  grid: CharacterGridSpec;
  iconDataUrl: string;
  frameRate: number;
  createdAt: number;
  /** Original upload for re-cropping in editor. */
  sourceSheetDataUrl?: string;
  frameLayout?: SkillFrameLayout;
  /** Cooldown reduction cards applied to this skill (−0.1s each). */
  cooldownCards?: number;
  /** Attack skill damage multiplier (default 1.2). */
  damageMultiplier?: number;
  /** Attack only — default single. */
  attackPattern?: SkillAttackPattern;
  /** Buff skill — temporary stat bonuses while active. */
  buffStatBonus?: Partial<Record<StatId, number>>;
  /** Buff only — AP cards applied (+SKILL_AP_PER_CARD each). */
  skillApCards?: number;
  /** Buff only — duration cards applied (+0.1s each). */
  buffDurationCards?: number;
  /** AOE attack only — range cards applied (+1% width each). */
  aoeRangeCards?: number;
};

export const DEFAULT_ATTACK_SKILL_DAMAGE_MULT = 1.2;

export type SkillBarState = {
  version: 3;
  slots: (string | null)[];
  library: SkillDefinition[];
  /** Auto-cast when fighting and cooldown ready (per hotbar slot). */
  slotAuto: boolean[];
  /** Hotbar master Auto: normal attack chase + skill auto when slots are filled. */
  masterAuto?: boolean;
};

export function createDefaultSkillBar(): SkillBarState {
  return {
    version: 3,
    slots: Array.from({ length: SKILL_SLOT_MAX }, () => null),
    library: [],
    slotAuto: Array.from({ length: SKILL_SLOT_MAX }, () => false),
    masterAuto: false,
  };
}

export function normalizeSkillKind(value: unknown): SkillKind {
  return value === "buff" ? "buff" : "attack";
}

export function normalizeSkillAttackPattern(value: unknown): SkillAttackPattern {
  return value === "aoe" ? "aoe" : "single";
}

export function defaultBuffDurationMs(): number {
  return SKILL_DEFAULT_BUFF_DURATION_MS;
}
